import { CollectionName, collectionAlias } from "@tc/search-document-builders"
import { serviceStructureBuilder } from "../utils/helper"
import {
  FieldDefinition,
  FieldDefinitionGetter,
  FieldUniqueIdentifier,
  GetServiceFieldPath,
  IFieldRegistry,
  IRegistryGetter,
  resolvePath,
  ServiceName,
  SubmissionLayout,
} from "../utils/types"
import { COLLECTION_TO_LIST_NAME, defaultFieldRegistry } from "./mappings"

/**
 * Regex to extract list_code from filter_by attribute.
 * Matches patterns like: "list_code := unitWeight" or "list_code:=unitVolume"
 */
const LIST_CODE_REGEX = /list_code\s*:=\s*(\w+)/

/**
 * Builds a map of FUID to list_code for fields that use the Unit collection.
 * This extracts the list_code dynamically from the filter_by attribute in searchRepresentation.
 *
 * The Unit collection has multiple list_codes:
 * - unitWeight: for gross/net weight unit fields
 * - unitVolume: for gross/net volume unit fields
 * - unitDuration: for process duration unit
 * - unitTemperature: for process temperature unit
 *
 * Fields with `dependOn` pattern are not included as their list_code is dynamic.
 *
 * @param layout - The submission layout (AP_EXPORT, AP_TRANSFER, PHYTO_EXPORT)
 * @param registry - The field registry to iterate
 * @returns Map of FUID to list_code (e.g., "unitWeight", "unitVolume")
 *
 * @see AC4/AC5 of Story #181912
 */
export const buildUnitFuidToListCodeMap = (
  layout: SubmissionLayout,
  registry: IFieldRegistry = defaultFieldRegistry,
): Map<string, string> => {
  const map = new Map<string, string>()
  const unitCollectionAlias = collectionAlias(CollectionName.Unit)

  for (const fieldDef of registry.values()) {
    const searchRep = fieldDef.searchRepresentation?.[layout]

    // Only process Unit collection fields
    if (searchRep?.collectionName !== unitCollectionAlias) continue

    // Skip fields with dependOn pattern - their list_code is dynamic
    if (searchRep.collectionSettings?.dependOn) continue

    const filterBy = searchRep.collectionSettings?.filter_by
    if (filterBy) {
      const match = LIST_CODE_REGEX.exec(filterBy)
      if (match?.[1]) {
        map.set(fieldDef.fuid, match[1])
      }
    }
  }

  return map
}

/**
 * Builds a map of FUID to list_name for fields that use value_list collections.
 * This is used by resolveListId() in submission-preview.ts as Step 3 fallback
 * for fields with sub_category = 'CSTM' or 'UN'.
 *
 * For Unit collection (UN subcategory), this function:
 * 1. Extracts list_code from filter_by using buildUnitFuidToListCodeMap()
 * 2. The caller must then lookup list_name from reference.list table using list_code
 *
 * For other collections (CSTM subcategory), uses COLLECTION_TO_LIST_NAME mapping.
 *
 * @param layout - The submission layout (AP_EXPORT, AP_TRANSFER, PHYTO_EXPORT)
 * @param registry - The field registry to iterate
 * @param listCodeToListNameMap - Optional map of list_code to list_name for Unit collection.
 *                                 If not provided, Unit FUIDs will not be included in the result.
 * @returns Map of FUID to list_name
 *
 * @see AC4 of Story #181912
 */
export const buildFuidToListNameMap = (
  layout: SubmissionLayout,
  registry: IFieldRegistry = defaultFieldRegistry,
  listCodeToListNameMap?: Map<string, string>,
): Map<string, string> => {
  const map = new Map<string, string>()
  const unitCollectionAlias = collectionAlias(CollectionName.Unit)

  for (const fieldDef of registry.values()) {
    const searchRep = fieldDef.searchRepresentation?.[layout]
    if (!searchRep?.collectionName) continue

    // Handle Unit collection separately - need list_code → list_name lookup
    if (searchRep.collectionName === unitCollectionAlias) {
      // Skip fields with dependOn pattern - their list_code is dynamic
      if (searchRep.collectionSettings?.dependOn) continue

      if (listCodeToListNameMap) {
        const filterBy = searchRep.collectionSettings?.filter_by
        if (filterBy) {
          const match = LIST_CODE_REGEX.exec(filterBy)
          if (match?.[1]) {
            const listCode = match[1]
            const listName = listCodeToListNameMap.get(listCode)
            if (listName) {
              map.set(fieldDef.fuid, listName)
            }
          }
        }
      }
    } else {
      // For other collections, use static COLLECTION_TO_LIST_NAME mapping
      const listName = COLLECTION_TO_LIST_NAME[searchRep.collectionName]
      if (listName) {
        map.set(fieldDef.fuid, listName)
      }
    }
  }

  return map
}

// --- Factory Function ---
/**
 * Creates a specific field definition getter bound to a registry.
 */
export function makeGetFieldDefinition(
  registryGetter: IRegistryGetter,
): FieldDefinitionGetter {
  return (fuid: FieldUniqueIdentifier): FieldDefinition | undefined => {
    return registryGetter.get(fuid)
  }
}

// --- Default Instance ---
/**
 * Default field getter instance configured with the defaultFieldRegistry.
 */
export const getField: FieldDefinitionGetter =
  makeGetFieldDefinition(defaultFieldRegistry)

// getFieldPath is a convenience function to get the path of a field by its FUID
export const getServiceFieldPath: GetServiceFieldPath =
  (serverName: ServiceName, layout?: SubmissionLayout) =>
  (fuid: FieldUniqueIdentifier): string => {
    const fieldDef = getField(fuid)
    if (fieldDef) {
      return (
        resolvePath(fieldDef.serviceRepresentations[serverName], layout) ??
        "<unknown>"
      )
    }
    return "<unknown>"
  }

// --- Exports ---
export default getServiceFieldPath // Export the default instance

// Also export the factory and registry for flexibility
export * as Fuid from "./fuid" // Re-export FUID constants for easier access
export { defaultFieldRegistry } from "./mappings"

export const buildServiceStructure =
  serviceStructureBuilder(defaultFieldRegistry)
